import { useCallback, useMemo } from "react"
import { Typography, Card, Box, Divider, useTheme, Button } from "@mui/material"
import _ from "lodash"
import EmailIcon from "@mui/icons-material/Email"
import { deepmerge } from "@mui/utils"

import { Link } from "react-router-dom"

import config from "config"

import { useTranslation } from "providers/i18n"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { Image, ImageModal } from "components/Image"
import { Carousel, CarouselItem } from "components/Carousel"
import { WebsiteIcon } from "components/icons"
import { FeatureLabel } from "components/domains/features/FeatureLabel"
import { HighlightFeatureCard } from "components/domains/events"

const DEFAULT_GALLERY_CAROUSEL_OPTIONS = {
  fixedWidth: "180px",
  fixedHeight: "120px",
  perPage: 1,
  gap: 12,
  padding: { left: 12, right: 12 },
}

const DEFAULT_PROMOTION_CAROUSEL_OPTIONS = {
  perPage: 1,
  fixedWidth: 310,
  fixedHeight: 168,
  gap: 12,
  padding: { left: 12, right: 12 },
}
export const FeatureDetail = ({
  feature,
  products = [],
  loading,
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const {
    root,
    listContainer,
    featureLabel,
    operationTimeLabel,
    section,
    galleryImage,
    divider,
    contactButton,
    sectionTitle,
    descriptionText,
    promotionCarouselItem,
    contactWrapper,
    productImage,
    productName,
    openingLabel,
  } = theme.components[`AppFeatureDetail`]?.styleOverrides || {}
  const {
    galleryCarouselOptions = DEFAULT_GALLERY_CAROUSEL_OPTIONS,
    productCarouselOptions,
  } = theme.components[`AppFeatureDetail`]?.defaultProps || {}

  const {
    websiteLink,
    phone,
    email,
    promotions,
    description,
    gallery,
    level,
    category,
    hours,
    isComingSoon,
    startDate,
  } = props

  const opening = startDate?.locale("en").format("MMM/YYYY") || "-"

  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const featurePromotions = useMemo(
    () =>
      promotions?.filter(({ properties }) =>
        _.includes(["store", "shop"], properties.category)
      ) || [],
    [promotions]
  )

  const hasProduct = Array.isArray(products) && products.length > 0

  const handleClickContact = useCallback(
    (contact) => {
      if (isKioskMode) return
      window.open(contact)
    },
    [isKioskMode]
  )

  if (loading) return null

  return (
    <>
      <Card className="FeatureDetail-root" sx={root}>
        <Box sx={listContainer}>
          <FeatureLabel label={t("Floor")} value={t(level)} sx={featureLabel} />
          <FeatureLabel
            label={t("Category")}
            value={t(category)}
            sx={featureLabel}
          />
          {isComingSoon ? (
            <FeatureLabel
              label={t("Opening")}
              value={opening}
              sx={openingLabel}
            />
          ) : (
            <FeatureLabel
              label={t("Operation Time")}
              value={hours || "-"}
              sx={operationTimeLabel}
            />
          )}
        </Box>
        {gallery && (
          <Box className="FeatureDetail-section" sx={section}>
            <Carousel
              options={deepmerge(
                DEFAULT_GALLERY_CAROUSEL_OPTIONS,
                galleryCarouselOptions
              )}
            >
              {gallery?.map((image, idx) => (
                <CarouselItem key={`${image.name}-${idx}`}>
                  <Image
                    onClick={() => handleImage(image.url)}
                    src={image.url}
                    alt={`${image.name}-${idx}`}
                    sx={galleryImage}
                  />
                </CarouselItem>
              ))}
            </Carousel>
          </Box>
        )}
        {hasProduct && (
          <Box className="FeatureDetail-section" sx={section}>
            <Typography component="h2" sx={sectionTitle}>
              {t("Recommended Menu")}
            </Typography>
            <Carousel options={productCarouselOptions}>
              {products.map((product) => {
                const coverImage = _.get(product, "properties.cover_image")
                const name = _.get(product, "properties.name")
                return (
                  <CarouselItem key={`${product.id}`}>
                    <Box>
                      <Image src={coverImage} sx={productImage} />
                      <Typography variant="subtitle2" sx={productName}>
                        {t(name)}
                      </Typography>
                    </Box>
                  </CarouselItem>
                )
              })}
            </Carousel>
          </Box>
        )}
        {/* end of product section */}
        {(websiteLink || phone || email) && (
          <>
            <Box className="FeatureDetail-section" sx={contactWrapper}>
              {websiteLink && (
                <>
                  <Button
                    variant="text"
                    onClick={() => handleClickContact(websiteLink)}
                    startIcon={<WebsiteIcon />}
                    sx={contactButton}
                  >
                    {websiteLink}
                  </Button>
                  <Divider sx={divider} />
                </>
              )}
              {email && (
                <>
                  <Button
                    variant="text"
                    onClick={() => handleClickContact(`mailto:${email}`)}
                    startIcon={<EmailIcon />}
                    sx={contactButton}
                  >
                    {email}
                  </Button>
                  <Divider sx={divider} />
                </>
              )}
            </Box>
          </>
        )}
        {description && (
          <>
            <Box className="FeatureDetail-section" sx={section}>
              <Typography component="h2" sx={sectionTitle}>
                {t("Description")}
              </Typography>
              <Typography variant="body2" sx={descriptionText}>
                {t(description)}
              </Typography>
            </Box>
          </>
        )}
        {featurePromotions?.length > 0 && (
          <>
            <Box className="FeatureDetail-section" sx={section}>
              <Carousel options={DEFAULT_PROMOTION_CAROUSEL_OPTIONS}>
                {promotions?.length > 0 &&
                  promotions.map((featurePromotion) => (
                    <CarouselItem
                      key={featurePromotion.id}
                      className={featurePromotion.properties?.category}
                      sx={promotionCarouselItem}
                    >
                      <Link
                        to={`/promotions/${featurePromotion.id}?redirectTo=-1`}
                        key={featurePromotion.id}
                      >
                        <HighlightFeatureCard feature={featurePromotion} />
                      </Link>
                    </CarouselItem>
                  ))}
              </Carousel>
            </Box>
          </>
        )}
      </Card>
      <ImageModal
        open={openModal}
        imageSrc={activeImage}
        onClose={handleClose}
      />
    </>
  )
}
